
import {Save, Settings, Search, GripVertical, X, Dot, CircleHelp, CircleAlert, Check, ChevronsUpDown, DiamondPlus, 
    MessageSquare, MessageSquareMore, ClipboardX,
    Eye,
    Pencil,
    PencilLine} from "lucide-react"
import {PropagateLoader} from "react-spinners"
import { Button } from "./ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@radix-ui/react-label"
import { Switch } from "@/components/ui/switch"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogTrigger,
    DialogClose,
  } from "@/components/ui/dialog"


import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
 
import { cn } from "@/lib/utils"

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
    HoverCard,
    HoverCardContent,
    HoverCardTrigger,
  } from "@/components/ui/hover-card"

import { ScrollArea } from "@/components/ui/scroll-area"
import { Textarea } from "@/components/ui/textarea"
import { useState, useEffect, useRef, SetStateAction, useContext, use, useCallback } from "react"
import {
    DndContext, 
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    PointerActivationConstraint,
    TouchSensor,
    MouseSensor,
  } from '@dnd-kit/core';
  import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable
  } from '@dnd-kit/sortable';
import {CSS as DNDCSS} from '@dnd-kit/utilities';
import { restrictToParentElement  } from '@dnd-kit/modifiers';
import { Link, useNavigate } from "react-router-dom"
import axios, { AxiosError } from "axios"
import { useDebouncedCallback } from 'use-debounce';
import { toast } from "sonner"
import { Toaster } from "./ui/sonner"
import { truncateText } from "@/utils/TextUtils"
import apiClient from "@/utils/ApiClient"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { UsernameContext } from "@/utils/ProtectedRoutes"
import { DeleteList, FetchList, PatchList } from "./ApiRoutes"
import MainLoader from "@/utils/Loaders"
import { motion } from "motion/react"


// define custom type for api tmdb results
export interface QueryResult {
    name?: string;
    title?: string;
    overview?: string;
    id: string;
    
    poster_path: string;
    release_date?: Date;
    first_air_date?: Date;
    images?:string[];
    artists?:{}[];
    album?:{}

}

interface ListItem {
    id?:string;
    external_id:string;
    title:string; 
    year?:string; 
    image_url:string;
    artist?:string;
    itemType?:"movies"|"shows"|"songs"|"artists"|"albums";
    comment?:string;
};

export default function ListEditor(){
    const [accessToken, setAccessToken] = useState(localStorage.getItem('accessToken'));

    const [listTitle, setListTitle] = useState("Super cool title");
    const [previousListTitle, setPreviousListTitle] = useState("");

    const [listDesc, setListDesc] = useState("a cool description about things");
    const [previousListDesc, setPreviousListDesc] = useState("");
    
    const [listType, setListType] = useState("movies");

    const [listTag, setListTag] = useState("");

    const [isPublic, setIsPublic] = useState(true);
    const [previousIsPublic, setPreviousIsPublic] = useState(true);

    const [isRanked, setIsRanked] = useState(true);
    const [previousIsRanked, setPreviousIsRanked] = useState(true);

    const [isDeleted, setIsDeleted] = useState(false);

    // used to determine if the list is being edited or created for the first time
    const [isPatch, setIsPatch] = useState(false);

    // query results loading state
    const [responseLoading, setResponseLoading] = useState(false);

    // submitting loading state
    const [isSubmitting, setIsSubmitting] = useState(false);

    // holds current search query used by the api
    const [query, setQuery] = useState("");

    // contains list of item objects
    const [items, setItems] =  useState<ListItem[]>([]);

    // we need this to compare against current items so we know whether to patch or not
    const [previousItems, setPreviousItems] = useState<ListItem[]>([]);
    
    const [queryResults, setQueryResults] = useState<QueryResult[]>([]);

    // search input reference
    const searchRef = useRef(null);

    // tan stack query client
    const queryClient = useQueryClient();
    
    const [listID, setListID] = useState<string|null>(null);

    const {username, setUsername} = useContext(UsernameContext)
    
    const navigate = useNavigate();

    interface PatchListVariables {
        listId: string;
        payload: {
            title: string;
            description: string;
            list_type: string;
            tag: string;
            is_public: boolean;
            is_ranked: boolean;
            items: ListItem[]; 
        };
        }

    const patchMutation = useMutation({
        mutationFn: (variables: PatchListVariables) => 
            PatchList(variables.listId, variables.payload),

        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['list', variables.listId] });
            toast.success("List updated!");
        },
        onError: (error) => {
            toast.error("Failed to update list.");
        }
    });

    const handlePatch = (event) => {
        event.preventDefault();
        if(items.length < 1){
            setIsSubmitting(false);
            toast.error("Lists can't be empty")
            return;
        }
        
        if(items.length < 3){

            setIsSubmitting(false);
            toast.error("Lists must have at least 3 items")
            return;
        }

        if(listTitle.length <= 3){
            setIsSubmitting(false);
            toast.error("Lists must have a title with at least 3 characters")
            return;
        }

        // if no changes to list detail are made, do not send a patch request
        if ( (previousIsRanked == isRanked && previousIsPublic == isPublic && 
            previousListTitle == listTitle && previousListDesc == listDesc) && 
            (JSON.stringify(previousItems) == JSON.stringify(items))) 
            {
                toast.error("You must change something before saving")
                return;
            }
            
        // when a change is made    
        else{
            setPreviousItems(items);
            setPreviousIsPublic(isPublic);
            setPreviousIsRanked(isRanked);
            setPreviousListTitle(listTitle);
            setPreviousListDesc(listDesc);
        }
        
        const currentPayload = {
            title: listTitle,
            description: listDesc,
            list_type: listType,
            tag: listTag,
            is_public: isPublic,
            is_ranked: isRanked,
            items: items,
    };
    


    patchMutation.mutate({ listId: listID ?? "", payload: currentPayload });
    };

    // debounces query so server doesnt implode
    const debounceQuery = useDebouncedCallback(
        // function
        (value) => {
            if(value.trim() == ""){
                setQueryResults([])
                return;
            }
            setQuery(value);

        },
        // delay in ms
        500
      );

    const { isPending, isError, data, error, isSuccess } = useQuery({
        queryKey: ['list', listID],
        queryFn: () => FetchList(listID ?? ""),
        enabled: !!listID,
    })

    // checks if list ID exists so state can be updated and user can "edit" an existing list
    useEffect(() => {
        const pathSegments = location.pathname.split('/');
        const editSegmentIndex = pathSegments.indexOf("edit");
        
        if(pathSegments[editSegmentIndex + 1]){
            setListID(pathSegments[editSegmentIndex + 1]);
        }
        else{
            setListID(null);
        }

    },[location.pathname])

   useEffect(() => {
        if (!listID) {
            setIsSubmitting(false);
            return;
        }
        if (isPending) {
            setIsSubmitting(true);
        } 
        else if (isError) {
            setIsPatch(false);
            setIsSubmitting(false);
            toast.error(`Unable to load list: ${error?.message}`);
        } 
        else if (isSuccess && data) {
            setListTitle(data.title);
            setPreviousListTitle(data.title);

            setListDesc(data.description);
            setPreviousListDesc(data.description);

            setListType(data.list_type);
            setListTag(data.tag || "");

            setIsPublic(data.is_public);
            setIsRanked(data.is_ranked);

            setItems(data.items || []);
            setPreviousItems(data.items || []);

            setQueryResults([]);
            setQuery("");

            setIsSubmitting(false);
            setIsPatch(true);
        } 
        else {
            setIsSubmitting(false);
        }
    }, [isPending, isError, data, error, isSuccess, listID]);


    // if the list type has changed, clear listitems
    useEffect(()=> {
        if(searchRef.current){
            searchRef.current.value = "";
        }
    },[items])

    
    // querys
    useEffect(()=> {
        const TMDB_URL = "/tmdb/search";
        const SPOTIFY_URL = "/spotify/search";

        const fetchData = async () => {
            let queryPath = "";
            if(listType == "movies" || listType == "shows"){
                queryPath = TMDB_URL;
            }
            else if(listType == "songs" || listType == "artists" || listType == "albums"){
                queryPath = SPOTIFY_URL;
            } 
            else {
                return;
            }

            if(query.trim() == ""){
                return;
            }

            try{
                setResponseLoading(true);
                const res = await apiClient.get(queryPath ,{
                    params: {
                        query: query,
                        list_type: listType
                    }
                })

                setResponseLoading(false);
                setQueryResults(res.data);
                
            }

            catch (err: AxiosError) {
                setResponseLoading(false);
               
            }
        }

      
        
        fetchData();
        
    }, [query])

        

    const handleSave = async (event) => {
        const listData = {
            title: listTitle,
            description: listDesc,
            list_type: listType, 
            tag: listTag,
            is_public: isPublic,
            is_ranked: isRanked,
            items: items
        };

        event.preventDefault();
        setIsSubmitting(true);
    
        
        if(items.length < 1){
            setIsSubmitting(false);
            toast.error("Lists can't be empty")
            return;
        }
        
        if(items.length < 3){

            setIsSubmitting(false);
            toast.error("Lists must have at least 3 items")
            return;
        }

        if(listTitle.length <= 3){
            setIsSubmitting(false);
            toast.error("Lists must have a title with at least 3 characters")
            return;
        }

        try {
            setIsSubmitting(true);
            const response = await apiClient.post('/lists/', listData, {
                headers: {
                    'Content-Type': 'application/json',
                }
            });
    
            if (response.status > 299) {
                setIsSubmitting(false);

                let errorData = { message: `HTTP error! status: ${response.status}` }; // Default error
                try {
                    errorData = await response.json();
                } catch (parseError) {
                  

                }
                const error = new Error(errorData.message || `HTTP error! status: ${response.status}`);
                error.response = response; 
                error.data = errorData;   
                throw error; 
            }

            setIsSubmitting(false);
            // success handling here
            
            setItems([]);
            setListTitle("Super cool title");
            setListDesc("a cool description about things");
            
            toast.success(`${listTitle} has been created!`);
            queryClient.refetchQueries({queryKey:["profileList", username]});
            setTimeout(() => {window.location.reload()}, 2500) // ideally this should be an spa flow
    
        } catch (error) {
            toast.error(`An issue has occured with our servers. Try again later. `);
        } finally {
            setIsSubmitting(false);
        }
    };


      const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 100,
                tolerance: 8,
            },
        }),

        useSensor(KeyboardSensor, {
          coordinateGetter: sortableKeyboardCoordinates,
        })
      );
    
    return(
        <>
        { /*quite literally makes the toast appear... like a toaster*/}
        <Toaster />
            <main className="relative flex flex-col items-center bg-foreground h-full w-full p-4 overflow-y-auto">
                { isSubmitting &&
                        <div className="absolute z-20 w-full h-full flex justify-center items-center backdrop-blur-md">
                            <div className="flex gap-10 flex-col justify-center items-center w-full h-full ">
                                <MainLoader />
                            </div>
                            
                        </div>
                }
             
                <header className="flex flex-col items-center relative max-w-300 w-8/12 min-w-85 ">
                
                    <div className="flex justify-between w-full">
                        <h1 className="font-bold text-2xl text-secondary">List Editor</h1>
                        {/*we need to switch submit function if PATCHing an existing list, or POSTing a new one as they are different routes with different data */}
                        <form onSubmit={isPatch ? handlePatch : handleSave}>
                        <div className="flex justify-center items-center gap-4">
                            {isPatch &&
                                <Button onClick={() => navigate(`/list/view/${listID}`)} type="button" className="flex justify-center font-semibold bg-secondary text-primary hover:bg-neutral-300 hover:cursor-pointer">
                                <Eye className="text-primary w-6 h-6 "/>
                                    View list
                                </Button>
                            }
                            <Button type="submit" className="flex justify-center font-semibold bg-secondary text-primary hover:bg-neutral-300 hover:cursor-pointer">
                            <Save className="text-primary w-6 h-6 "/>
                                Save
                            </Button>

                        </div>

                        </form>
                    </div>
                    
                    <section className="flex justify-between bg-neutral-800 mt-6 p-4 w-full max-w-300 min-w-85 min-h-20 h-fit rounded-lg">
                        <div className="flex flex-col">
                            <h2 className="text-secondary font-semibold text-lg">{listTitle}</h2>
                            <p className="text-neutral-500 wrap-break-word">{listDesc}</p>
                            <div className="flex mt-2 gap-1 cursor-default flex-wrap">
                                {listType && <Badge className="text-sm capitalize">{listType}</Badge>}
                                
                                { isRanked && <Badge className="text-sm">Ranked</Badge> }
                                { isPublic ? <Badge className="text-sm">Public</Badge> : <Badge className="text-sm">Private</Badge>}
                                
                                {listTag && <ListTagBadge tagName={listTag} />}
                            </div>
                        </div>
                        <ListSettingsDialogue listID={listID ?? ""} title={listTitle} setTitle={setListTitle} description={listDesc} setDescription={setListDesc}
                        isPublic={isPublic} setIsPublic={setIsPublic} isRanked={isRanked} setIsRanked={setIsRanked}
                        setTag={setListTag} tag={listTag} setType={setListType} type={listType} setItems={setItems} isPatch={isPatch} isDeleted={isDeleted} setIsDeleted={setIsDeleted}/>
                    </section>
                </header>

                <section className="relative flex flex-col mt-6 justify-center items-center max-w-300 w-8/12 min-w-85 gap-4">
                    { isDeleted &&
                        <article className="relative flex flex-col w-full items-center ">
                        <Input disabled={true} ref={searchRef} id="item-search" placeholder="make or edit another list to search" defaultValue={query}
                        className="flex pl-10 h-12 text-sm sm:text-base rounded-2xl text-secondary selection:bg-neutral-800 max-w-full hover:cursor-not-allowed"/>
                        <Search className="absolute text-neutral-600 top-2.5 left-2.5"/>
                        
                    </article>
                    }

                    {!isDeleted &&
                        <article className="relative flex flex-col w-full items-center ">
                            <Input ref={searchRef} id="item-search" placeholder="oh I know you wanna search something" defaultValue={query} onInput={e => debounceQuery(e.currentTarget.value)}
                            className="flex pl-10 h-12 text-sm sm:text-base rounded-2xl text-secondary selection:bg-neutral-800 max-w-full"/>
                            <Search className="absolute text-secondary top-2.5 left-2.5"/>

                            <div className="flex pt-2 w-full">
                                {
                                queryResults && <QueryResultsContainer items={items} setItems={setItems} results={queryResults} setResults={setQueryResults} listType={listType} 
                                query={query} setQuery={setQuery} responseLoading={responseLoading}/>
                                }
                            </div>

                            
                        </article>
                    }
                    
                    { isDeleted &&
                        <section className="flex flex-col items-center justify-center w-full max-w-300 bg-red-500/20 p-4 rounded-lg">
                            <CircleAlert className="w-10 h-10 text-red-500" />
                            <h3 className="text-red-500 font-semibold text-lg">This list has been deleted</h3>
                            <p className="text-neutral-500">You can <span className="underline hover:font-semibold hover:cursor-pointer" onClick={() => {navigate("/list/edit/"); window.location.reload(); }}>create a new one</span> or edit an existing one.</p>
                        </section>

                    }
                    { !isDeleted && 
                        <section className="flex flex-col mt-6 p w-full max-h-fit overflow-auto gap-4">
                        <DndContext 
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                            modifiers={[restrictToParentElement]}
                            >
                            <SortableContext 
                                items={items.map(item => item.external_id)}
                                strategy={verticalListSortingStrategy}
                            >
                                { items.length > 0 &&
                                    items.map((item,i) => {
                                        return(
                                            <NewListItem external_id={item.external_id} id={item.id} key={item.external_id} title={item.title} artist={item.artist || "" } 
                                            image={item.image ?? item.image_url} position={i+1} year={item.year} items={items} setItems={setItems} comment={item.comment}/>
                                        )
                                    })
                                }

                                {
                                    items.length == 0 &&
                                    <section className="flex flex-col items-center justify-center text-neutral-600">
                                        <ClipboardX className="w-20 h-20 text-neutral-600" />
                                        <h3 className="font-semibold text-xl">Your list is empty</h3>
                                        <p className="font-semibold text-base">Add some stuff!</p>

                                    </section>
                                }
                            </SortableContext>
                        </DndContext>
                        </section>
                    }
                    
                    
                    
                </section>
            </main>
        </>
    )


    function handleDragEnd(event:DragEndEvent) {
        const {active, over} = event;
    
        if (active.id !== over.id) {
          setItems((items) => {
            // Find the actual array index of the items using their IDs
            const oldIndex = items.findIndex(item => item.external_id === active.id);
            const newIndex = items.findIndex(item => item.external_id === over.id);
    
            // Check if both indices were found before attempting to move
            if (oldIndex !== -1 && newIndex !== -1) {
               return arrayMove(items, oldIndex, newIndex);
            }
            // Return original items if indices weren't found (optional safety check)
            return items;
          });
        }
      }
}

interface QueryContainerProps {
    listType: string,
    results: QueryResult[];
    
    setResults: React.Dispatch<React.SetStateAction<QueryResult[]>>;
    items:ListItem[];
    setItems:React.Dispatch<React.SetStateAction<ListItem[]>>;
    query:string;
    setQuery:React.Dispatch<React.SetStateAction<string>>;
    responseLoading:boolean;
}

function QueryResultsContainer( {results, setResults, listType, items, setItems, query, setQuery, responseLoading}:QueryContainerProps ){
    let allItemID:string[] = items.map(item => item.external_id);

    useEffect(() => {
        allItemID = items.map(item => item.external_id);
    },[items])

    const handleClick = (result:QueryResult) => {

        let isDuplicate:boolean = false;

        setResults([]);
        setQuery("");

        // check to see if item is already in list
        items.forEach((item, index)=> {
            if(item.external_id == result.id){
                isDuplicate = true;
            }
        })

        if(isDuplicate){
            return;
        }

        let backdrop = "https://image.tmdb.org/t/p/w500/"+ result.poster_path;
        
        // update items state depending on list type
        if(listType == "movies" && result.title){
            setItems([...items,{
                external_id:result.id,
                title:result.title,
                year:String(result.release_date).split("-")[0],
                image_url:backdrop,
                itemType:listType,
    
            }])
        }

        else if(listType == "shows" && result.name){
            setItems([...items,{
                external_id:result.id,
                title:result.name,
                year:String(result.first_air_date).split("-")[0],
                image_url:backdrop,
                itemType:listType,
            }])
        }

        // spotify uses arrays for images in different sizes 0 = 640x640, 1 = 320x320, 2 = 160x160
        else if(listType == "artists" && result.name){
            setItems([...items,{
                external_id:result.id,
                title:result.name,
                image_url:result.images[0].url,
                itemType:listType,

            }])
        }

        else if(listType == "albums" && result.name){
            setItems([...items,{
                external_id:result.id,
                title:result.name,
                year:String(result.release_date).split("-")[0],
                image_url:result.images[0].url,
                artist:result.artists[0].name,
                itemType:listType,
            }])
        }

        else if(listType == "songs" && result.name){
            setItems([...items,{
                external_id:result.id,
                title:result.name,
                year:String(result.album.release_date).split("-")[0],
                image_url:result.album.images[0].url,
                artist:result.artists[0].name,
                itemType:listType,
            }])
        }
    }

    

    if(query.trim() != "" && results.length < 1 && responseLoading == false){
        return(
            <ScrollArea className="absolute z-30 top-1 flex flex-col w-full h-fit jus max-h-100 overflow-auto pr-2 bg-none backdrop-blur-xs rounded-b-xl">
                <article className="flex min-h-40 bg-none w-full justify-center text-accent bg-neutral-950 duration-150 ease-in-out hover:bg-primary p-2 border-b-2 border-neutral-800">
                    <div className="flex flex-col justify-center items-center text-center">
                        <h5 className="text-2xl font-bold text-neutral-500">No results :(</h5>
                        <p className="text-md text-neutral-600">Don't shoot the messenger... learn to spell</p>
                    </div>
                </article>
            </ScrollArea>    
        )
    }

    // show loading animation while waiting for promise
    if (responseLoading == true){
        return(
            <ScrollArea className="absolute z-30 top-1 flex flex-col w-full h-fit jus max-h-100 overflow-auto pr-2 bg-none backdrop-blur-xs rounded-b-xl">
                    <article className="flex min-h-40 bg-none w-full justify-center text-accent bg-neutral-950 duration-150 ease-in-out hover:bg-primary p-2 border-b-2 border-neutral-800">
                        <div className="flex flex-col justify-center items-center text-white">
                            <PropagateLoader color="#ffffff"></PropagateLoader>
                        </div>
                    </article>
                </ScrollArea>
        )  
    }


    // conditional rendering for each media type as they have different attributes
    switch(listType){
        // conditional rendering TMDB
        case "movies":
            return(
                <ScrollArea className="absolute z-30 flex flex-col w-full h-fit jus max-h-100 overflow-auto pr-2 bg-none backdrop-blur-xs rounded-b-xl">
                    { 
                        results.map((result, index) => (
                            <>
                            {
                                result.poster_path &&
                            
                            <article key={index} className="flex min-h-40 bg-none w-full text-accent bg-neutral-950 duration-150 ease-in-out hover:bg-primary p-2 border-b-2 border-neutral-800">
                                <div className="relative flex w-full items-center">
                                    <img src={`https://image.tmdb.org/t/p/w500/${result.poster_path}`} className="rounded-lg h-40" />
                                    <div className="flex flex-col items-start justify-center w-full px-4 text-left">
                                        <h5 className=" font-bold text-md max-w-2/3 md:text-lg ">{result.title}</h5>
                                        { result.overview &&
                                            <p className="max-w-60 pr-4 sm:pr-0 text-xs md:text-base">{truncateText(result.overview,75)}</p>
                                        }
                                        {
                                            allItemID.includes(result.id) ?
                                            <Check className="absolute right-0 top-4/10 w-7 h-7 cursor-pointer focus:text-neutral-400" onClick={() => toast.error("This item is already in your list")}/>
                                            :
                                            <DiamondPlus className="absolute right-0 top-4/10 w-7 h-7 cursor-pointer focus:text-neutral-400 hover:text-neutral-400" onClick={() => handleClick(result)}/>
                                        }
                                       
                                    </div>
                                </div>
                            </article>
                            }
                            </>
                        ))
                    }
                </ScrollArea>    
            )
        
        case "shows":
            return(
                <ScrollArea className="absolute z-30 flex flex-col w-full h-fit jus max-h-100 overflow-auto pr-2 bg-none backdrop-blur-xs rounded-b-xl">
                    { 
                        results.map((result, index) => (
                            <>
                            {
                                result.poster_path &&
                            
                            <article key={index} className="flex min-h-40 bg-none w-full text-accent bg-neutral-950 duration-150 ease-in-out hover:bg-primary p-2 border-b-2 border-neutral-800">
                                <div className="relative flex w-full items-center">
                                    <img src={`https://image.tmdb.org/t/p/w500/${result.poster_path}`} className="rounded-lg h-40" />
                                    <div className="flex flex-col items-start justify-center w-full px-4 text-left">
                                        <h5 className=" font-bold text-md md:text-lg ">{result.name}</h5>
                                        { result.overview &&
                                            <p className="max-w-60 text-xs md:text-base">{truncateText(result.overview,75)}</p>
                                        }
                                        {
                                            allItemID.includes(result.id) ?
                                            <Check className="absolute right-0 top-4/10 w-7 h-7 cursor-pointer focus:text-neutral-400" onClick={() => toast.error("This item is already in your list")}/>
                                            :
                                            <DiamondPlus className="absolute right-0 top-4/10 w-7 h-7 cursor-pointer focus:text-neutral-400 hover:text-neutral-400" onClick={() => handleClick(result)}/>
                                        }
                                    </div>
                                </div>
                            </article>
                            }
                            </>
                        ))
                    }
                </ScrollArea>    
            )
        // conditional rendering SPOTIFY
        case "albums":
            return(
                <ScrollArea className="absolute z-30 flex flex-col w-full h-fit jus max-h-100 overflow-auto pr-2 bg-none backdrop-blur-xs rounded-b-xl">
                    { 
                        results.map((result, index) => (
                            <>
                            {
                               result.images && result.images.length >= 1 && result.artists &&
                            
                            <article key={index} className="flex min-h-40 bg-none w-full text-accent bg-neutral-950 duration-150 ease-in-out hover:bg-primary p-2 border-b-2 border-neutral-800">
                                <div className="relative flex w-full items-center">
                                    <img src={result.images[0].url} className="rounded-lg h-40" />
                                    <div className="flex flex-col items-start justify-center w-full px-4 text-left">
                                        <h5 className="max-w-3/4 font-bold text-md md:text-lg">{result.name}</h5>
                                        { result.artists[0] &&
                                            <p className="max-w-60 text-xs md:text-base">
                                                <span className="flex flex-col sm:flex-row justify-center items-left sm:items-center"> 
                                                    <p>{result.artists[0].name}</p> <Dot className="hidden sm:inline" /> <p className="text-neutral-500 mt-1 sm:mt-0 font-semibold "> {String(result.release_date).split("-")[0]}</p>
                                                </span>
                                            </p>
                                        }
                                        {
                                            allItemID.includes(result.id) ?
                                            <Check className="absolute right-0 top-4/10 w-7 h-7 cursor-pointer focus:text-neutral-400" onClick={() => toast.error("This item is already in your list")}/>
                                            :
                                            <DiamondPlus className="absolute right-0 top-4/10 w-7 h-7 cursor-pointer focus:text-neutral-400 hover:text-neutral-400" onClick={() => handleClick(result)}/>
                                        }
                                    </div>
                                </div>
                            </article>
                            }
                            </>
                        ))
                    }
                </ScrollArea>    
            )

        case "artists":
            return(
                <ScrollArea className="absolute z-30 flex flex-col w-full h-fit jus max-h-100 overflow-auto pr-2 bg-none backdrop-blur-xs rounded-b-xl">
                    { 
                        results.map((result, index) => (
                            <>
                            {
                                result.images && result.images.length >= 1 &&
                            
                            <article key={index} className="flex min-h-40 bg-none w-full text-accent bg-neutral-950 duration-150 ease-in-out hover:bg-primary p-2 border-b-2 border-neutral-800">
                                <div className="relative flex w-full items-center">
                                    <img src={result.images[0].url} className="rounded-lg h-35 w-35" />
                                    <div className="flex flex-col items-start justify-center w-full px-4 text-left">
                                        <h5 className=" font-bold text-md md:text-lg wrap-break-word max-w-25 sm:max-w-full">{result.name}</h5>
                                       {
                                            allItemID.includes(result.id) ?
                                            <Check className="absolute right-0 top-4/10 w-7 h-7 cursor-pointer focus:text-neutral-400" onClick={() => toast.error("This item is already in your list")}/>
                                            :
                                            <DiamondPlus className="absolute right-0 top-4/10 w-7 h-7 cursor-pointer focus:text-neutral-400 hover:text-neutral-400" onClick={() => handleClick(result)}/>
                                        }
                                    </div>
                                </div>
                            </article>
                            }
                            </>
                        ))
                    }
                </ScrollArea>    
            )
        case "songs":
            return(
                <ScrollArea className="absolute z-30 flex flex-col w-full h-fit max-h-100 overflow-auto pr-2 bg-none backdrop-blur-xs rounded-b-xl">
                    { 
                        results.map((result, index) => (
                            <>
                            {
                                result.album && result.album.images.length >= 1 && result.artists &&
                            
                            <article key={index} className="flex min-h-40 bg-none w-full text-accent bg-neutral-950 duration-150 ease-in-out hover:bg-primary p-2 border-b-2 border-neutral-800">
                                <div className="relative flex w-full items-center">
                                    <img src={result.album.images[0].url} className="rounded-lg h-40" />
                                    <div className="flex flex-col items-start justify-center w-full px-4 text-left">
                                        <h5 className=" max-w-25 md:max-w-50 wrap-break-word font-bold text-md md:text-lg ">{result.name}</h5>
                                        <p className="max-w-60 text-xs md:text-base">
                                                <span className="flex flex-col sm:flex-row justify-center items-left sm:items-center"> 
                                                    <p>{result.artists[0].name}</p> <Dot className="hidden sm:inline" /> <p className="text-neutral-500 mt-1 font-semibold sm:mt-0"> {String(result.album.release_date).split("-")[0]}</p>
                                                </span>
                                            </p>
                                       {
                                            allItemID.includes(result.id) ?
                                            <Check className="absolute right-0 top-4/10 w-7 h-7 cursor-pointer focus:text-neutral-400" onClick={() => toast.error("This item is already in your list")}/>
                                            :
                                            <DiamondPlus className="absolute right-0 top-4/10 w-7 h-7 cursor-pointer focus:text-neutral-400 hover:text-neutral-400" onClick={() => handleClick(result)}/>
                                        }
                                    </div>
                                </div>
                            </article>
                            }
                            </>
                        ))
                    }
                    <script src="https://open.spotify.com/embed/iframe-api/v1" async></script>
                </ScrollArea>    
                )
        
        default:
            return;

    }
    
}

interface NewListItemProps {
    id?: string,
    external_id:string,
    title:string,
    image:string,
    year:string,
    position?:Number,
    artist?:string,
    items:ListItem[];
    comment?:string;
    setItems:React.Dispatch<React.SetStateAction<ListItem[]>>;

}

function NewListItem({id, title="Adventure Time", image="http://image.tmdb.org/t/p/w500//qk3eQ8jW4opJ48gFWYUXWaMT4l.jpg",
    position, artist, year, items, setItems, comment, external_id}:NewListItemProps) {

    const [itemComment, setItemComment] = useState(comment ?? "");

    // on comment change, duplicate array then update item comment in new array, then push array
    useEffect(() => {
    setItems(currentItems => {
        const thisItemIndex = currentItems.findIndex(object => object.external_id === external_id);

        
        if (thisItemIndex === -1) {
            return currentItems;
        }

        const newItems = [...currentItems];
        const updatedItem = { ...newItems[thisItemIndex], comment: itemComment };
        newItems[thisItemIndex] = updatedItem; 
        
        return newItems; 
    });
}, [itemComment, external_id]);



    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({
        id: external_id,
        transition: {
            duration: 300, // milliseconds
            easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
        }
    });
    
    const style = {
        transform: DNDCSS.Transform.toString(transform),
        transition,
        };

    const deleteSelf = (selfExternalID=external_id) => {
        setItems(items.filter(item=> item.external_id !== selfExternalID))
    }


    return(
        <div ref={setNodeRef} className="flex w-full " style={style} >
            <article className="relative flex hover:cursor-default w-full min-h-32 rounded-2xl bg-center ease-in-out duration-200  " 
            style={{ backgroundImage: `url(${image})` }}>

                <div className="relative group flex justify-center h-full w-full rounded-xl inset-0 bg-gradient-to-t duration-175 ease-in-out hover:bg-black/40 bg-black/60 from-black/70 to-transparent z-0 hover:backdrop-blur-none backdrop-blur-xs ">
                    <div className="flex gap-1 justify-center items-center absolute left-2 top-2.5">
                        <span {...attributes} {...listeners} className="p-4 hover:cursor-grab active:cursor-grabbing"><GripVertical  className="group-hover:text-neutral-300  text-neutral-500 h-6 w-6 mt-2"/></span>
                        {position && <h4 className="group-hover:text-shadow-lg text-8xl font-bold group-hover:text-white/50 text-white/20">{`${position}`}</h4> }
                    </div>
                    
                    <div className="flex flex-col justify-center items-center text-secondary">
                        <h3 className="group-hover:text-shadow font-semibold text-base sm:text-lg max-w-45 text-center sm:max-w-50 md:max-w-70 lg:max-w-100">{title}</h3>
                        <div className="group-hover:text-shadowd flex justify-center items-center w-max-40 z-30">
                            { artist && <p className="text-neutral-400 font-semibold">{artist}</p> }
                            { artist && <Dot /> }
                            <p className="text-neutral-400 text-sm sm:text-base font-semibold">{year}</p>
                        </div>

                        <div className="flex mt-2 ">
                                <CommentDialogue comment={itemComment} setComment={setItemComment}  />
                        </div>
                        
                    </div>

                    <div className="absolute right-2 top-11 flex text-secondary">
                        <X className="ml-2 h-6 w-6 z-30 hover:cursor-pointer" onClick={() => {
                            deleteSelf();
                        }} />
                    </div>
                    
                </div>
                
            </article>
            
        </div>
        
    )
}

interface CommentDialogueProps {
    comment:string;
    setComment: React.Dispatch<SetStateAction<string>>
}

function CommentDialogue({comment, setComment}:CommentDialogueProps) {
    const commentInput = useRef<HTMLTextAreaElement>(null); 

    const handleCommentSave = () => {
        if(commentInput.current && commentInput.current.value.trim() == ""){
            setComment("");
            return;
        }

        if(commentInput.current){
            setComment(commentInput.current.value);
            return;
        }
    }

    return(
        <>
            <Dialog>
                <DialogTrigger>
                    {
                    
                    comment ? <MessageSquareMore className=" text-white/60 group-hover:text-white ease-in-out duration-100 w-5 h-5 hover:w-6 hover:h-6 hover:cursor-pointer" />  
                    : <MessageSquare className=" text-white/60 group-hover:text-white ease-in-out duration-100 w-5 h-5 hover:w-6 hover:h-6 hover:cursor-pointer" />
                    }
                </DialogTrigger>
                <DialogContent className="bg-foreground text-secondary">
                    <DialogHeader>
                    <DialogTitle>Item comment</DialogTitle>
                    <DialogDescription>
                        What makes this item special?
                    </DialogDescription>
                    </DialogHeader>
                    <div className="flex flex-col">
                        <Label htmlFor="comment-input" className="text-sm">Your Comment</Label>
                        <Textarea id="comment-input" ref={commentInput} className="resize-none text-sm" maxLength={100} rows={4} onChange={e => setComment(e.target.value)} defaultValue={comment} placeholder="Psssst. This is where you'd add a lil' comment." />
                    </div>
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button className="hover:cursor-pointer w-full" onClick={handleCommentSave} type="button">Apply</Button>
                        </DialogClose>
                    </DialogFooter>
                </DialogContent>
                
            </Dialog>
        </>
    )
}

interface ListSettingsProps {
    isDeleted: boolean; 
    setIsDeleted: React.Dispatch<React.SetStateAction<boolean>>; // used for updating ui when deleted

    listID: string; // used for deleting lists

    title: string;
    setTitle: React.Dispatch<React.SetStateAction<string>>;

    description: string;
    setDescription: React.Dispatch<React.SetStateAction<string>>;

    type: string;
    setType: React.Dispatch<React.SetStateAction<string>>;

    tag: string;
    setTag: React.Dispatch<React.SetStateAction<string>>;

    isPublic: boolean;
    setIsPublic: React.Dispatch<React.SetStateAction<boolean>>;

    isRanked: boolean;
    setIsRanked: React.Dispatch<React.SetStateAction<boolean>>;

    setItems: React.Dispatch<React.SetStateAction<ListItem[]>>;

    isPatch: boolean; // used for deleting lists, as you cant delete a list that hasnt been created

}

function ListSettingsDialogue({
    listID, 
    title, 
    setTitle, 
    description, 
    setDescription, 
    tag,
    setTag,
    type,
    setType,
    isPublic, 
    setIsPublic,
    isRanked, 
    setIsRanked,
    setItems,
    isPatch,
    isDeleted,
    setIsDeleted
  }: ListSettingsProps) {

    // change everything locally, then save on apply click so unsaved changes dont change list details
    const [localIsPublic,setLocalIsPublic] = useState(false);
    const [localIsRanked,setLocalIsRanked] = useState(false);
    const [localTitle,setLocalTitle] = useState(title);
    const [localDesc,setLocalDesc] = useState(description);

    const [localType,setLocalType] = useState(type);
    const [localTag,setLocalTag] = useState(tag);

    const [listTypeChanged, setListTypeChanged] = useState(false);
    const [deleteState,setDeleteState] = useState(false);

    const [dialogOpen, setDialogOpen] = useState(false)
    const queryClient = useQueryClient();
    const {username, setUsername} = useContext(UsernameContext);

    const [pencilState, setPencilState] = useState<"hover"|"not-hover">("not-hover")

    const navigate = useNavigate();
    // on open, reset all values to current list details, as previous local states will be still saved
    useEffect(() => {
        if (dialogOpen) {
            setLocalIsPublic(isPublic)
            setLocalIsRanked(isRanked)
            setLocalTitle(title)
            setLocalDesc(description)
            setLocalType(type);
            setLocalTag(tag);
        }
    }, [dialogOpen])

    // set parent state to equal local state
    const handleApplyClick = () => {
        if (type !== localType) {
            setItems([]);
        }
        setIsRanked(localIsRanked);
        setIsPublic(localIsPublic);
        setTitle(localTitle);
        setDescription(localDesc);
        setTag(localTag);
        setType(localType);
    }

    const handleDeleteClick = () => {
        if (type !== localType) {
            setItems([]);
        }
        setIsRanked(localIsRanked);
        setIsPublic(localIsPublic);
        setTitle(localTitle);
        setDescription(localDesc);
        setTag(localTag);
        setType(localType);
        setDeleteState(true)
        
    }

    const deleteMutation = useMutation({
        mutationFn: (id: string) => DeleteList(id),
        onSuccess: () => {
            toast.success(`${title} has been deleted!`);
            queryClient.invalidateQueries({ queryKey: ["profileList", username] });
            setDialogOpen(false);
            setIsDeleted(true);
        },
        onError: (error) => {
            toast.error(`Failed to delete list. Please try again.`);
        },
    });
    
    const handleDeleteConfirm = () => {
        if (listID) {
            deleteMutation.mutate(listID);
        }
    };
    
    return(
        <div className="group text-white hover:text-neutral-300 hover:bg-neutral-800 rounded-full">
            <Dialog open={dialogOpen} onOpenChange={(open) => {setDialogOpen(open); setDeleteState(false);}}>
                <DialogTrigger onMouseEnter={() => setPencilState("hover")} onMouseLeave={() => setPencilState("not-hover")}>
                    {
                        pencilState == "hover" ? 
                        <motion.div
                        initial={{x:0, rotate:0}}
                            animate={{
                                x:[-1,-3,-4, -6],
                                rotate:[-4, -6, -1, -3, -6, -1, -2 ],
                                transition: {
                                    repeat: Infinity,
                                    repeatType: "reverse",
                                    type:"tween",
                                    ease:"easeInOut",
                                    duration:0.75,
                            },
                        }} 
                        >
                            <PencilLine className="duration-225 ease-in-out w-6 h-6 hover:cursor-pointer scale-110" />
                        </motion.div>
                        :
                        <Pencil className="duration-225 ease-in-out w-6 h-6 hover:cursor-pointer" />

                    }
                </DialogTrigger>
                {
                    !deleteState &&
                    <DialogContent className="flex flex-col justify-center bg-foreground text-secondary">
                    <DialogHeader>
                        <DialogTitle>List Settings</DialogTitle>
                        <DialogDescription>
                            Switch up your list settings here. Hit apply when finished to save your new settings.
                        </DialogDescription>
                    </DialogHeader>
                    <div> 
                        <Label htmlFor="list-title"> Title <span className="text-sm text-neutral-500">(max 50 characters)</span> </Label>
                        <Input className="max-w-70 sm:max-w-full" maxLength={50} id="list-title" defaultValue={title} onChange={e => setLocalTitle(e.currentTarget.value)}/>
                    </div>
                    <div>
                        <Label htmlFor="list-desc"> Description <span className="text-sm text-neutral-500">(max 75 characters)</span> </Label>
                        <Input className="max-w-70 sm:max-w-full" maxLength={75} id="list-desc" defaultValue={description} onChange={e => setLocalDesc(e.currentTarget.value)} />
                    </div>
                    <div className="flex sm:justify-between items-center gap-6 sm:gap-2">
                        <div> 
                            <Label htmlFor="is-public"> Make list public </Label>
                            <p className="text-sm text-neutral-500">Make your list visible to friends and foes</p>
                        </div>
                        <Switch checked={localIsPublic} onCheckedChange={setLocalIsPublic} className={`data-[state=checked]:bg-green-700 data-[state=unchecked]:bg-red-700 hover:cursor-pointer`} id="is-public" />
                    </div>

                    <div className="flex sm:justify-between items-center gap-6 sm:gap-2">
                        <div>
                            <Label htmlFor="is-ranked"> Make list ranked </Label>
                            <p className="text-sm text-neutral-500 ">Allows you to create the ultimate tier list</p>
                        </div>
                        <Switch checked={localIsRanked} onCheckedChange={setLocalIsRanked} className="data-[state=checked]:bg-green-700 data-[state=unchecked]:bg-red-700 hover:cursor-pointer" id="is-ranked" />
                        
                    </div>

                    <div className="flex items-center gap-2 sm:gap-4 justify-center">

                        <div className="flex flex-col items-center gap-1">
                            <Label className="flex items-center gap-2" htmlFor="list-type"> List type 
                                <HoverCard>
                                        <HoverCardTrigger>
                                            <span><CircleHelp className="w-4 h-4" /> </span> 
                                        </HoverCardTrigger>
                                        <HoverCardContent className="bg-foreground text-background">
                                            Defines which media type your list is. It can only be of one type (for now).
                                        </HoverCardContent>
                                </HoverCard>
                            </Label>
                            <Select defaultValue={type} onValueChange={value => {
                                setLocalType(value)
                                setListTypeChanged(true);
                                }}>
                                <SelectTrigger id="list-type" className=" sm:w-40 ">
                                    <SelectValue placeholder="Choose?" />
                                </SelectTrigger>
                                <SelectContent className="bg-black text-secondary">
                                    <SelectItem className="hover:cursor-pointer" value="albums">Albums</SelectItem>
                                    <SelectItem className="hover:cursor-pointer" value="artists">Artists</SelectItem>
                                    <SelectItem className="hover:cursor-pointer" value="songs">Songs</SelectItem>
                                    <SelectItem className="hover:cursor-pointer" value="shows">Shows</SelectItem>
                                    <SelectItem className="hover:cursor-pointer" value="movies">Movies</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="flex flex-col items-center gap-1 pointer-events-auto">
                            <Label className="flex items-center gap-2" htmlFor="list-tag"> List tag 
                                <HoverCard>
                                    <HoverCardTrigger>
                                        <span><CircleHelp className="w-4 h-4" /> </span> 
                                    </HoverCardTrigger>
                                    <HoverCardContent className="bg-foreground text-background">
                                        A custom tag to define the atmosphere or purpose of your list. <br />
                                        
                                        <span className="text-sm text-neutral-500">(i.e if Invincible was in it, it would be <Badge className="text-sm bg-purple-800 text-white">Peak</Badge>)</span> 
                                    </HoverCardContent>
                                </HoverCard>
                            </Label>
                            <Combobox parentTag={localTag} setParentTag={setLocalTag} />
                        </div>
                        
                    </div>
                        {
                            listTypeChanged == true && localType != type && 
                            <span className="flex justify-center items-center gap-1 text-sm text-red-800"><CircleAlert className="h-5 w-5 pt-0.5" /> Changing your list type will clear any items from your list</span>
                        }

                 
                        
                            {
                                isPatch ?
                                    <div className="flex gap-1 w-full justify-center"> 
                                    
                                        <Button className="hover:cursor-pointer w-1/2 bg-red-900 hover:bg-red-800" onClick={handleDeleteClick} type="button">Delete list</Button>
                                    <DialogClose asChild>
                                        <Button className="hover:cursor-pointer w-1/2" onClick={handleApplyClick} type="button">Apply</Button>
                                    </DialogClose>
                                    </div>
                                    :
                                    <DialogClose asChild>
                                        <Button className="hover:cursor-pointer w-full" onClick={handleApplyClick} type="button">Apply</Button>
                                    </DialogClose>
                            }
                            
                            
                        
                        
                    
                    </DialogContent>
                }
                 {deleteState &&
                <DialogContent className="flex flex-col justify-center items-center bg-foreground text-secondary">
                    <DialogHeader>
                        <DialogTitle>Are you sure?</DialogTitle>
                        <DialogDescription>
                            This will permanently delete your list. This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <section className="flex justify-center gap-4">
                        <Button className="w-1/2" onClick={() => setDeleteState(false)}>No, I'm scared</Button>
                    
                        <Button
                            
                            className="hover:cursor-pointer w-1/2 bg-red-900 hover:bg-red-800"
                            disabled={deleteMutation.isPending} 
                            onClick={handleDeleteConfirm}
                        >
                            {deleteMutation.isPending ? 'Deleting...' : "Yes, I'm sure"}
                        </Button>
                    </section>
                </DialogContent>
            }
                    
            </Dialog>
                
        </div>
    )
}

interface ComboBoxProps {
    selections?: {
        value:string,
        label:string,
     }[];
    
    parentTag: string;
    setParentTag: React.Dispatch<React.SetStateAction<string>>;
}

export const defaultTagSelections = [
    { value: "peak", label: "Peak", color:"bg-violet-800" },
    { value: "weird", label: "Weird", color:"bg-emerald-800" },
    { value: "funny", label: "Funny", color:"bg-yellow-700" },
    { value: "what is that melody?!", label: "What is that melody?!", color:"bg-indigo-800" },
    { value: "artsy", label: "Artsy", color:"bg-rose-800" },
    { value: "fact", label: "Fact", color:"bg-blue-800" },
    { value: "random", label: "Random", color:"bg-red-800" },
    { value: "current", label: "Current", color:"bg-teal-800" },
    { value: "inspiring", label: "Inspiring", color:"bg-pink-600" },
    { value: "chill", label: "Chill", color:"bg-blue-900" },
    { value: "hype", label: "Hype", color:"bg-orange-600" },
    { value: "maidenless", label: "Maidenless", color:"bg-fuchsia-900" },
    { value: "allergic to grass", label: "Allergic to grass", color:"bg-lime-900" },

]

export function Combobox({ selections = defaultTagSelections, parentTag, setParentTag }: ComboBoxProps) {
    
    const [open, setOpen] = useState(false)
    const [value, setValue] = useState(parentTag)
  
    const selectedLabel = selections.find((tag) => tag.value === value)?.label

    useEffect(()=>{
        setParentTag(value)
    },[value])


    return (
      <Popover open={open} onOpenChange={setOpen} modal={true}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            
            className="justify-between bg-transparent hover:text-muted-foreground hover:bg-transparent text-muted-foreground border-accent " 
          >
            {selectedLabel ? <span className="text-secondary">{selectedLabel}</span> : "Select a tag..." }
            
            
            <ChevronsUpDown className="opacity-50" />
          </Button>
        </PopoverTrigger>
        
        <PopoverContent className="sm:w-40 bg-black text-secondary">
          <Command className=" bg-transparent text-secondaryf">
            <CommandInput placeholder="Search tags..." className="h-9 text-white" />
            <CommandList className="">
              <CommandEmpty>No tags found.</CommandEmpty>
              <CommandGroup>
                {selections.map((selection) => (
                  <CommandItem
                    className={cn(
                        "cursor-pointer px-2 py-1.5 rounded-sm transition-colors",
                        "hover:bg-white hover:text-black",
                         " text-white" )}

                    key={selection.value}
                    value={selection.value}
                    onSelect={(currentValue) => {
                        setValue(currentValue === value ? "" : currentValue)
                        setOpen(false)
                    }}
                  >
                    {selection.label}
                    <Check
                      className={cn(
                        "ml-auto",
                        value === selection.value ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    )
}

interface ListTagProps{
    selections?:{
        label:string;
        value:string;
        color:string;
    }[];
    tagName:string;
}

function ListTagBadge({selections=defaultTagSelections, tagName}:ListTagProps){
    const tagIndex = selections.findIndex(object => object.value == tagName);
    const tagColor = selections[tagIndex].color;
    
    return(
    <>
        <Badge className={`text-sm ${tagColor} text-white capitalize`}>{tagName}</Badge>
    </>
    )
}
